/**
 * Saved Filters Database Operations
 *
 * CRUD operations for saved filter views.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleNotFoundError } from './helpers';
import type { FilterState, GroupingState, SortingState } from '@/types';

export interface SavedFilter {
  id: string;
  userId: string;
  spaceId: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  filters: FilterState;
  grouping: GroupingState | null;
  sorting: SortingState | null;
  isDefault: boolean;
  isPinned: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSavedFilterData {
  userId: string;
  spaceId?: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  filters: FilterState;
  grouping?: GroupingState;
  sorting?: SortingState;
  isDefault?: boolean;
  isPinned?: boolean;
}

export interface UpdateSavedFilterData {
  name?: string;
  description?: string;
  icon?: string;
  color?: string;
  filters?: FilterState;
  grouping?: GroupingState | null;
  sorting?: SortingState | null;
  isDefault?: boolean;
  isPinned?: boolean;
}

// Supabase row type for saved_filters table
interface SupabaseSavedFilterRow {
  id: string;
  user_id: string;
  space_id: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  filters: FilterState;
  grouping: (GroupingState & { collapsedGroups?: string[] }) | null;
  sorting: SortingState | null;
  is_default: boolean;
  is_pinned: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

// Convert from Supabase (snake_case) to SavedFilter (camelCase)
function convertFilterFromSupabase(row: SupabaseSavedFilterRow): SavedFilter {
  // Handle collapsedGroups in grouping - convert array to Set
  const grouping: GroupingState | null = row.grouping ? {
    ...row.grouping,
    collapsedGroups: new Set(row.grouping.collapsedGroups || []),
  } : null;

  return {
    id: row.id,
    userId: row.user_id,
    spaceId: row.space_id,
    name: row.name,
    description: row.description,
    icon: row.icon,
    color: row.color,
    filters: row.filters,
    grouping,
    sorting: row.sorting,
    isDefault: row.is_default,
    isPinned: row.is_pinned,
    usageCount: row.usage_count,
    lastUsedAt: row.last_used_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Convert grouping state for storage (Set -> Array)
function prepareGroupingForStorage(grouping: GroupingState | null | undefined): (GroupingState & { collapsedGroups?: string[] }) | null {
  if (!grouping) return null;
  return {
    ...grouping,
    collapsedGroups: grouping.collapsedGroups instanceof Set
      ? Array.from(grouping.collapsedGroups)
      : grouping.collapsedGroups,
  } as GroupingState & { collapsedGroups?: string[] };
}

/**
 * Get all saved filters for a user
 */
export async function getSavedFilters(userId: string, spaceId?: string): Promise<SavedFilter[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('saved_filters')
      .select('*')
      .eq('user_id', userId)
      .order('is_pinned', { ascending: false })
      .order('usage_count', { ascending: false })
      .order('name', { ascending: true });

    if (spaceId) {
      query = query.or(`space_id.eq.${spaceId},space_id.is.null`);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertFilterFromSupabase);
}

/**
 * Get pinned saved filters for quick access
 */
export async function getPinnedFilters(userId: string): Promise<SavedFilter[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('saved_filters')
      .select('*')
      .eq('user_id', userId)
      .eq('is_pinned', true)
      .order('name', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertFilterFromSupabase);
}

/**
 * Get a single saved filter
 */
export async function getSavedFilter(id: string): Promise<SavedFilter | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('saved_filters')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertFilterFromSupabase(data);
}

/**
 * Get the default saved filter for a user/space
 */
export async function getDefaultFilter(userId: string, spaceId?: string): Promise<SavedFilter | null> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('saved_filters')
      .select('*')
      .eq('user_id', userId)
      .eq('is_default', true);

    if (spaceId) {
      query = query.eq('space_id', spaceId);
    } else {
      query = query.is('space_id', null);
    }

    return query.single();
  });

  if (error) return handleNotFoundError(error, null);
  return convertFilterFromSupabase(data);
}

/**
 * Create a new saved filter
 */
export async function createSavedFilter(data: CreateSavedFilterData): Promise<SavedFilter> {
  const insertData = {
    user_id: data.userId,
    space_id: data.spaceId || null,
    name: data.name,
    description: data.description || null,
    icon: data.icon || null,
    color: data.color || null,
    filters: data.filters,
    grouping: prepareGroupingForStorage(data.grouping),
    sorting: data.sorting || null,
    is_default: data.isDefault || false,
    is_pinned: data.isPinned || false,
  };

  // If this is set as default, unset other defaults
  if (data.isDefault) {
    await withRetry(() =>
      supabase
        .from('saved_filters')
        .update({ is_default: false })
        .eq('user_id', data.userId)
        .eq('is_default', true)
    );
  }

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('saved_filters')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !created) throw error || new Error('Failed to create saved filter');
  return convertFilterFromSupabase(created);
}

/**
 * Update a saved filter
 */
export async function updateSavedFilter(id: string, updates: UpdateSavedFilterData): Promise<SavedFilter> {
  const updatePayload: Record<string, unknown> = {};

  if (updates.name !== undefined) updatePayload.name = updates.name;
  if (updates.description !== undefined) updatePayload.description = updates.description;
  if (updates.icon !== undefined) updatePayload.icon = updates.icon;
  if (updates.color !== undefined) updatePayload.color = updates.color;
  if (updates.filters !== undefined) updatePayload.filters = updates.filters;
  if (updates.grouping !== undefined) updatePayload.grouping = prepareGroupingForStorage(updates.grouping);
  if (updates.sorting !== undefined) updatePayload.sorting = updates.sorting;
  if (updates.isDefault !== undefined) updatePayload.is_default = updates.isDefault;
  if (updates.isPinned !== undefined) updatePayload.is_pinned = updates.isPinned;

  // If setting as default, unset other defaults
  if (updates.isDefault) {
    const filter = await getSavedFilter(id);
    if (filter) {
      await withRetry(() =>
        supabase
          .from('saved_filters')
          .update({ is_default: false })
          .eq('user_id', filter.userId)
          .eq('is_default', true)
          .neq('id', id)
      );
    }
  }

  const { data, error } = await withRetry(() =>
    supabase
      .from('saved_filters')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to update saved filter');
  return convertFilterFromSupabase(data);
}

/**
 * Delete a saved filter
 */
export async function deleteSavedFilter(id: string): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('saved_filters')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
  return true;
}

/**
 * Record usage of a saved filter (for popularity sorting)
 */
export async function recordFilterUsage(id: string): Promise<void> {
  const filter = await getSavedFilter(id);
  if (!filter) return;

  await withRetry(() =>
    supabase
      .from('saved_filters')
      .update({
        usage_count: (filter.usageCount || 0) + 1,
        last_used_at: new Date().toISOString(),
      })
      .eq('id', id)
  );
}

/**
 * Toggle pin status
 */
export async function toggleFilterPin(id: string): Promise<SavedFilter> {
  const filter = await getSavedFilter(id);
  if (!filter) throw new Error('Filter not found');

  return updateSavedFilter(id, { isPinned: !filter.isPinned });
}

/**
 * Set as default filter
 */
export async function setAsDefaultFilter(id: string): Promise<SavedFilter> {
  return updateSavedFilter(id, { isDefault: true });
}

/**
 * Duplicate a saved filter
 */
export async function duplicateSavedFilter(id: string, newName?: string): Promise<SavedFilter> {
  const original = await getSavedFilter(id);
  if (!original) throw new Error('Filter not found');

  return createSavedFilter({
    userId: original.userId,
    spaceId: original.spaceId || undefined,
    name: newName || `${original.name} (Copy)`,
    description: original.description || undefined,
    icon: original.icon || undefined,
    color: original.color || undefined,
    filters: original.filters,
    grouping: original.grouping || undefined,
    sorting: original.sorting || undefined,
    isDefault: false,
    isPinned: false,
  });
}

// ============================================================================
// Preset Filters
// ============================================================================

/**
 * Create default preset filters for a new user
 */
export async function createDefaultPresets(userId: string): Promise<SavedFilter[]> {
  const presets: CreateSavedFilterData[] = [
    {
      userId,
      name: 'My Tasks',
      icon: 'user',
      filters: {
        status: [],
        assignees: [], // Will be populated with current user
        tags: [],
        priority: [],
        search: '',
        path: null,
        dateRange: { start: null, end: null },
        hasAttachments: null,
        hasSubtasks: null,
        showCompleted: false,
      },
      isPinned: true,
    },
    {
      userId,
      name: 'Due Today',
      icon: 'calendar',
      filters: {
        status: [],
        assignees: [],
        tags: [],
        priority: [],
        search: '',
        path: null,
        dateRange: {
          start: new Date(new Date().setHours(0, 0, 0, 0)),
          end: new Date(new Date().setHours(23, 59, 59, 999)),
        },
        hasAttachments: null,
        hasSubtasks: null,
        showCompleted: false,
      },
      isPinned: true,
    },
    {
      userId,
      name: 'High Priority',
      icon: 'flag',
      filters: {
        status: [],
        assignees: [],
        tags: [],
        priority: ['urgent', 'high'],
        search: '',
        path: null,
        dateRange: { start: null, end: null },
        hasAttachments: null,
        hasSubtasks: null,
        showCompleted: false,
      },
      isPinned: false,
    },
  ];

  const created: SavedFilter[] = [];
  for (const preset of presets) {
    const filter = await createSavedFilter(preset);
    created.push(filter);
  }

  return created;
}
